import { Controller } from "@hotwired/stimulus"
import * as bootstrap from "bootstrap"

// Connects to data-controller="modal"
export default class extends Controller {
  static values = {
    show: Boolean,
    static: Boolean,
    emptyStreamWrapper: String,
    multipleModal: Boolean,
  }

  connect() {
    if (this.showValue) {
      // When one #generalModal is turbo-swapped for another (e.g. publish
      // confirm → celebration → QR), the previous modal's Bootstrap instance +
      // backdrop aren't torn down, so backdrops stack and leave a stuck overlay.
      // Clear any leftover backdrops / lingering modal instances before showing,
      // unless this modal is explicitly meant to stack (multipleModal).
      if (!this.multipleModalValue) this.clearStaleModals()
      this.modalInstance.show()
      // Sweep any extra backdrops left by a turbo-swapped predecessor (its
      // backdrop lives on <body>, so it survives the #generalModal innerHTML
      // replace). Run after show settles AND on a short delay to catch
      // transition-end stragglers. Keep only the last backdrop.
      if (!this.multipleModalValue) {
        this.element.addEventListener("shown.bs.modal", this.dedupeBackdrops, { once: true })
        setTimeout(this.dedupeBackdrops, 400)
      }
    }

    if (this.emptyStreamWrapperValue === "true") {
      this.element.addEventListener("hidden.bs.modal", this.onHidden)
    }
  }

  disconnect() {
    this.element.removeEventListener("hidden.bs.modal", this.onHidden)
  }

  onHidden = () => {
    if (!this.multipleModalValue) {
      this.clearWrapperAndBackdrop()
    }
  }

  closeModal() {
    if (this.element.classList.contains("show")) {
      this.modalInstance.hide()
    } else {
      this.clearWrapperAndBackdrop()
    }
  }

  closePreviousModal() {
    const modals = document.querySelectorAll(".modal")
    if (modals.length > 1) {
      for (let i = 0; i < modals.length - 1; i++) {
        bootstrap.Modal.getInstance(modals[i])?.hide()
        modals[i].remove()
      }
    }
  }

  // Tear down any modal instances/backdrops left over from a previous
  // #generalModal occupant before a turbo-swapped modal shows, so backdrops
  // don't stack. Excludes THIS element (it's about to show).
  clearStaleModals() {
    document.querySelectorAll(".modal.show").forEach((m) => {
      if (m === this.element) return
      bootstrap.Modal.getInstance(m)?.dispose()
    })
    document.querySelectorAll(".modal-backdrop").forEach((bd) => bd.remove())
    document.body.classList.remove("modal-open")
    document.body.style.removeProperty("overflow")
    document.body.style.removeProperty("padding-right")
  }

  // Keep only the most recent backdrop (Bootstrap adds one per show; a
  // turbo-swapped predecessor can leave an orphan on <body>). If THIS modal is
  // no longer showing (it was swapped/closed), remove all backdrops and unlock
  // the body so no overlay is left stuck.
  dedupeBackdrops = () => {
    const stillOpen = document.querySelector(".modal.show")
    const backdrops = document.querySelectorAll(".modal-backdrop")
    const keep = stillOpen ? backdrops.length - 1 : backdrops.length
    for (let i = 0; i < keep; i++) backdrops[i].remove()
    if (!stillOpen) {
      document.body.classList.remove("modal-open")
      document.body.style.removeProperty("overflow")
      document.body.style.removeProperty("padding-right")
    }
  }

  clearWrapperAndBackdrop() {
    const wrapper = document.getElementById("generalModal")
    if (wrapper) wrapper.innerHTML = ""

    document.querySelectorAll(".modal-backdrop").forEach((backdrop) => backdrop.remove())
    document.body.classList.remove("modal-open")
    document.body.style.removeProperty("overflow")
    document.body.style.removeProperty("padding-right")
  }

  get modalInstance() {
    return bootstrap.Modal.getOrCreateInstance(this.element)
  }
}
